import React, { useRef, useState } from 'react';
import { Upload, X, AlertCircle, Loader2 } from 'lucide-react';
import { CloudinaryError, uploadToCloudinary } from '../lib/cloudinary';
import type { CloudinaryResponse, QuoteCardData } from '../types';

interface ImageUploadProps {
  quoteData: QuoteCardData;
  onUploadComplete: (response: { cloudinary: CloudinaryResponse; quote: QuoteCardData }) => void;
  onUploadError: (error: string) => void;
}

export default function ImageUpload({ quoteData, onUploadComplete, onUploadError }: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    setError(null);
    setIsUploading(true);

    try {
      const response = await uploadToCloudinary(file, quoteData);
      setPreviewUrl(response.cloudinary.url);
      onUploadComplete(response);
    } catch (err) {
      const errorMessage = err instanceof CloudinaryError ? err.message : 'Failed to upload image';
      setError(errorMessage);
      onUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const clearUpload = () => {
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-700 hover:text-red-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div
        className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <label
              htmlFor="file-upload"
              className="cursor-pointer text-blue-600 hover:text-blue-500"
            >
              Upload a file
              <input
                id="file-upload"
                ref={fileInputRef}
                type="file"
                className="sr-only"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                disabled={isUploading}
              />
            </label>
            <p className="mt-1 text-gray-500">or drag and drop</p>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            JPG, PNG, or WebP up to 5MB
          </p>
        </div>
      </div>

      {isUploading && (
        <div className="flex items-center justify-center gap-2 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Uploading...</span>
        </div>
      )}

      {previewUrl && !isUploading && (
        <div className="relative">
          <img
            src={previewUrl}
            alt="Preview"
            className="max-h-48 rounded-lg"
          />
          <button
            onClick={clearUpload}
            className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}